import React, {createContext, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import * as _ from 'lodash';
import {isNullOrUndefined, labels} from '../../../../util/utils';
import {clientSideEventNames} from '../../../../util/uiconstants';
import {setFormBodySaveState} from '../../../../actions/Form/formBodyActions';
import {
	anyBodyHasUnsavedChanges,
	getFormBodyByIdSlice,
	bodyHasUnsavedChanges
} from '../../../../selectors/Forms/formBodySelectors';
import CustomNavigationPrompt from '../../UnsavedChangesModal/CustomNavigationPrompt';
import {useIsAresView} from '../../../../util/Ares/customHooks';

export const FormBodyChangesContext = createContext({});
FormBodyChangesContext.displayName = 'FormBodyChangesContext';

export default function FormBodyChangesWatcher({children, afterNavigationConfirm}) {
	const dispatch = useDispatch();
	const isAresView = useIsAresView();

	const hasChanges = useSelector((state) => anyBodyHasUnsavedChanges(state));

	const formBodiesById = useSelector((state) => getFormBodyByIdSlice(state));

	const resetChanges = useCallback(
		(bodyId, elementName) => dispatch(setFormBodySaveState(bodyId, false, elementName)),
		[dispatch]
	);

	const onBodyValueChanged = useCallback(
		(bodyId, initialValue, newValue, elementName) => {
			let _initialValue;
			let _newValue;
			if (isNullOrUndefined(initialValue)) {
				_initialValue = '';
			} else {
				_initialValue = typeof initialValue === 'number' ? initialValue.toString() : initialValue;
			}

			if (isNullOrUndefined(newValue)) {
				_newValue = '';
			} else {
				_newValue = typeof newValue === 'number' ? newValue.toString() : newValue;
			}

			const valueChangedPrev = bodyHasUnsavedChanges(formBodiesById[bodyId], elementName);
			const valueChanged = !_.isEqual(_initialValue, _newValue);

			let promise = Promise.resolve();

			// Only dispatch the action if modified status changed for the body
			// For some reason, valueChangedPrev is always undefined when called from Relate Risk Factor to Risk body type
			if (valueChangedPrev !== valueChanged) {
				promise = dispatch(setFormBodySaveState(bodyId, valueChanged, elementName));
			}

			return promise;
		},
		[dispatch, formBodiesById]
	);

	const handleAfterNavigationCancel = useCallback(() => {
		setTimeout(() => {
			/*put focus back on this on form if the user clicked on something else*/
			document.activeElement.blur();
			window.dispatchEvent(new CustomEvent(clientSideEventNames.unsavedChangesCancel, {}));
		}, 10);
	}, [dispatch]);

	const handleAfterNavigationConfirm = useCallback(() => {
		window.dispatchEvent(new CustomEvent(clientSideEventNames.unsavedChangesConfirm, {}));
		afterNavigationConfirm && afterNavigationConfirm();
	}, [afterNavigationConfirm]);

	return (
		<FormBodyChangesContext.Provider value={{onBodyValueChanged, resetChanges}}>
			{children}
			<CustomNavigationPrompt
				afterCancel={handleAfterNavigationCancel}
				afterConfirm={handleAfterNavigationConfirm}
				showPrompt={hasChanges}
				isAresView={isAresView}
				title={labels.unsavedChangesTitle}
				message={labels.unsavedPageChangesMessage}
				closeTitle={labels.unsavedChangesStay}
				confirmButtonTitle={labels.unsavedChangesLeave}
			/>
		</FormBodyChangesContext.Provider>
	);
}

FormBodyChangesWatcher.propTypes = {
	children: PropTypes.node,
	afterNavigationConfirm: PropTypes.func
};
